import { useState, useEffect } from 'react';
import apiClient from '@/core/apiClient';
import { ExcelFilterDropdown } from '@/components/ui/excel-filter';

export interface ChartOfAccount {
  id: number;
  code: string;
  name: string;
  description: string;
  type: string;
  balance: number;
}

export function ChartSearch() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ChartOfAccount[]>([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    type: '',
    minBalance: null as number | null,
    maxBalance: null as number | null,
  });

  const handleSearch = async () => {
    if (!query.trim()) {
      setResults([]);
      return;
    }

    setLoading(true);
    try {
      const response = await apiClient.get('/comptabilitat/chart/search', {
        params: {
          q: query,
          type: filters.type || undefined,
          minBalance: filters.minBalance || undefined,
          maxBalance: filters.maxBalance || undefined,
        },
      });
      setResults(response.data?.data || []);
    } catch (error) {
      console.error('[v0] Error searching:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (query.trim()) {
        handleSearch();
      } else {
        setResults([]);
      }
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [query, filters]);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold">Cerca del Pla de Comptes</h1>
        <p className="text-gray-600">Cerca comptes per codi, nom o descripció</p>
      </div>

      <div className="space-y-3">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Cerca per codi, nom o descripció..."
          className="w-full px-4 py-2 border rounded text-sm"
        />

        <div className="flex gap-3 flex-wrap">
          <ExcelFilterDropdown
            label="Tipus"
            options={[
              { value: 'ACTIVO', label: 'Actiu' },
              { value: 'PASIVO', label: 'Passiu' },
              { value: 'EQUITY', label: 'Capital' },
              { value: 'INGRESO', label: 'Ingrés' },
              { value: 'GASTO', label: 'Despesa' },
            ]}
            selected={filters.type ? filters.type.split(',') : []}
            onApply={(values) => setFilters({ ...filters, type: values.join(',') })}
          />

          <input
            type="number"
            placeholder="Saldo mínim"
            value={filters.minBalance || ''}
            onChange={(e) =>
              setFilters({
                ...filters,
                minBalance: e.target.value ? parseFloat(e.target.value) : null,
              })
            }
            className="px-3 py-2 border rounded"
          />

          <input
            type="number"
            placeholder="Saldo màxim"
            value={filters.maxBalance || ''}
            onChange={(e) =>
              setFilters({
                ...filters,
                maxBalance: e.target.value ? parseFloat(e.target.value) : null,
              })
            }
            className="px-3 py-2 border rounded"
          />
        </div>
      </div>

      {loading && <p className="text-gray-600">Cercant...</p>}

      {results.length === 0 && query.trim() && !loading && (
        <p className="text-gray-500">No s&apos;han trobat comptes</p>
      )}

      <div className="space-y-2">
        {results.map((account) => (
          <div key={account.id} className="p-4 border rounded hover:bg-gray-50 cursor-pointer">
            <div className="flex justify-between items-start">
              <div>
                <div className="font-bold text-sm">
                  {account.code} - {account.name}
                </div>
                <div className="text-sm text-gray-600">{account.description}</div>
                <div className="text-xs text-gray-500 mt-1">Tipus: {account.type}</div>
              </div>
              <div className="text-right font-mono text-sm">
                {account.balance.toLocaleString('ca-ES', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
